import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Since there's no dedicated contact endpoint, we'll use inquiry with minimal fields
      await axios.post(`${API}/inquiry`, {
        name: formData.name,
        email: formData.email,
        phone: '',
        destination: 'General Inquiry',
        travel_dates: 'N/A',
        num_travelers: 1,
        budget_range: 'N/A',
        special_preferences: formData.message,
      });
      toast.success('Thank you for contacting us! We\'ll respond within 24 hours.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="contact-page"
    >
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />

        <div className="relative z-10 container-padding text-center max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-6"
          >
            Get in Touch
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9] text-primary"
            data-testid="contact-title"
          >
            Contact Us
          </motion.h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding container-padding" data-testid="contact-content">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-8">
                Let\'s Begin Your Journey
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed mb-12">
                Whether you have a specific destination in mind or need inspiration, our luxury travel
                advisors are here to craft your perfect journey.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <Mail className="text-accent mt-1" size={24} />
                  <div>
                    <h3 className="font-heading text-xl text-primary mb-2">Email</h3>
                    <a
                      href="mailto:reservations@sarvatrajourneys.com"
                      className="text-muted-foreground hover:text-accent transition-colors duration-300"
                    >
                      reservations@sarvatrajourneys.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <Phone className="text-accent mt-1" size={24} />
                  <div>
                    <h3 className="font-heading text-xl text-primary mb-2">Phone</h3>
                    <div className="space-y-2">
                      <a
                        href="tel:+917310630006"
                        className="text-muted-foreground hover:text-accent transition-colors duration-300 block"
                      >
                        +91 - 7310630006
                      </a>
                      <a
                        href="tel:+919871769006"
                        className="text-muted-foreground hover:text-accent transition-colors duration-300 block"
                      >
                        +91 - 9871769006
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-address">
                  <MapPin className="text-accent mt-1" size={24} />
                  <div>
                    <h3 className="font-heading text-xl text-primary mb-2">Office</h3>
                    <p className="text-muted-foreground">
                      Uttar Pradesh
                      <br />
                      India
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-12 border-t border-white/5">
                <h3 className="font-heading text-2xl text-primary mb-4">Office Hours</h3>
                <p className="text-muted-foreground">
                  Monday - Friday: 10:00 AM - 7:00 PM IST
                  <br />
                  Saturday: 11:00 AM - 5:00 PM IST
                  <br />
                  Sunday: Closed
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <div className="glass-card p-8 md:p-10">
                <h3 className="font-heading text-3xl font-light text-primary mb-8">
                  Send a Message
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="underline-input w-full px-0 py-4 text-primary font-body"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="underline-input w-full px-0 py-4 text-primary font-body"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="underline-input w-full px-0 py-4 text-primary font-body resize-none"
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full justify-center inline-flex items-center gap-3"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <Send size={16} />}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ContactPage;